/**
 * BlueBridge — Adaptateur ITERCATUS → module Blue.
 * -----------------------------------------------------------------
 * Isole le module Blue de la logique métier ITERCATUS. Blue ne connait
 * PAS l'existence d'ITERCATUS. Ce composant :
 *   1. lit les données réelles de l'app (useAuth, useLocation, useTranslation)
 *   2. les normalise dans le format contextProvider attendu par Blue
 *   3. exclut Blue des pages back-office / kiosque
 *
 * Blue est monté UNE SEULE FOIS au niveau global (App.js).
 * Le contexte est ré-évalué à chaque envoi de message (getPageContext fn),
 * donc suit la navigation SPA + le changement de langue.
 *
 * Phase E2 (2026-09-21) — UI simplifiée (pas de bulle « Besoin d'aide »,
 * pas de suggestions) + locale i18n propagée au backend Blue + UI 6 langues.
 */
import { useCallback, useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BlueAssistant } from "../blue-chat";
import { useAuth } from "../../context/AuthContext";

// Routes où Blue est masqué (back-office/admin + coming-soon + kiosque).
const EXCLUDED_PATTERNS = [
  "/dashboard",
  "/dashboard/*",
  "/coming-soon",
  "/verify-member/*",
  "/upload",
  "/edit/*",
  "/import-draft",
];

function isExcludedRoute(pathname) {
  return EXCLUDED_PATTERNS.some((pat) => {
    if (pat.endsWith("/*")) {
      const base = pat.slice(0, -2);
      return pathname === base || pathname.startsWith(base + "/");
    }
    return pathname === pat;
  });
}

function deriveContext(pathname, authState, locale) {
  const base = { locale: locale || "fr", pageType: "unknown", userStatus: "guest" };
  const { user, isMember } = authState || {};
  if (user) {
    base.userStatus = isMember ? "member" : "user";
    if (user.membership_status) base.membershipStatus = user.membership_status;
  }
  if (pathname === "/") return { ...base, pageType: "home", title: "Home" };
  if (pathname === "/association" || pathname === "/a-propos")
    return { ...base, pageType: "about", title: "About" };
  if (pathname === "/adhesion" || pathname === "/adherer")
    return { ...base, pageType: "membership", title: "Membership" };
  if (pathname === "/privileges")
    return { ...base, pageType: "privileges_list", title: "Privileges" };
  const privilegeMatch = matchPath("/privileges/:id", pathname);
  if (privilegeMatch)
    return { ...base, pageType: "privilege_detail", title: "Privilege", privilegeId: privilegeMatch.params.id };
  const destMatch = matchPath("/destination/:slug", pathname);
  if (destMatch) {
    const raw = destMatch.params.slug || "";
    const title = raw.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return { ...base, pageType: "destination", title, destination: title };
  }
  const artMatch = matchPath("/article/:id", pathname);
  if (artMatch)
    return { ...base, pageType: "article", title: "Article", articleId: artMatch.params.id };
  const tripMatch = matchPath("/carnets-de-voyage/:slug", pathname);
  if (tripMatch) {
    const raw = tripMatch.params.slug || "";
    const title = raw.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return { ...base, pageType: "trip", title, tripSlug: raw };
  }
  const tripDayMatch = matchPath("/carnets-de-voyage/:tripSlug/journee/:daySlug", pathname);
  if (tripDayMatch)
    return {
      ...base, pageType: "trip_day",
      title: `Day · ${tripDayMatch.params.tripSlug}`,
      tripSlug: tripDayMatch.params.tripSlug, daySlug: tripDayMatch.params.daySlug,
    };
  if (pathname === "/carnets-de-voyage")
    return { ...base, pageType: "carnets_list", title: "Travel journals" };
  if (pathname === "/bons-plans") return { ...base, pageType: "deals", title: "Deals" };
  if (pathname.startsWith("/tests-produits"))
    return { ...base, pageType: "product_reviews", title: "Product reviews" };
  if (pathname === "/connexion") return { ...base, pageType: "auth_login", title: "Login" };
  if (pathname === "/inscription") return { ...base, pageType: "auth_register", title: "Register" };
  if (pathname === "/espace-membre") return { ...base, pageType: "member_area", title: "Member area" };
  if (pathname === "/entreprises" || pathname === "/partenaires")
    return { ...base, pageType: "partners", title: "Partners" };
  if (pathname.startsWith("/categorie/")) {
    const raw = pathname.replace("/categorie/", "");
    return { ...base, pageType: "category", title: raw.replace(/-/g, " ") };
  }
  if (pathname === "/recherche") return { ...base, pageType: "search", title: "Search" };
  if (pathname === "/mentions-legales" || pathname === "/confidentialite")
    return { ...base, pageType: "legal", title: "Legal" };
  return { ...base, pageType: "other", title: pathname, path: pathname };
}

export default function BlueBridge() {
  const location = useLocation();
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "fr";

  const getPageContext = useCallback(
    () => deriveContext(location.pathname, { user: auth?.user, isMember: auth?.isMember }, locale),
    [location.pathname, auth?.user, auth?.isMember, locale]
  );
  const pageContext = useMemo(() => getPageContext(), [getPageContext]);

  // Config Blue localisée à chaud selon i18n.language. Le composant se
  // remonte via `key={locale}` afin que le message d'accueil, le placeholder,
  // le fallback etc. reflètent immédiatement la langue active.
  const blueConfig = useMemo(
    () => ({
      // UI simplifiée Phase E2 : pas de bulle externe, pas de suggestions.
      showTagline: false,
      tagline: "",
      suggestions: [],
      // Textes localisés depuis i18n (namespace `blue.*`).
      name: t("blue.name", "Blue"),
      greeting: t("blue.greeting"),
      strings: {
        inputPlaceholder: t("blue.inputPlaceholder"),
        sendLabel: t("blue.sendLabel"),
        closeLabel: t("blue.closeLabel"),
        openLabel: t("blue.openLabel"),
        thinkingLabel: t("blue.thinkingLabel"),
        onlineLabel: t("blue.onlineLabel"),
        suggestionsLabel: t("blue.suggestionsLabel"),
        errorLabel: t("blue.errorLabel"),
        emptyReplyLabel: t("blue.emptyReplyLabel"),
        aiDisclosureShort: t("blue.aiDisclosureShort"),
        aiDisclosureFull: t("blue.aiDisclosureFull"),
        aiDisclosureMore: t("blue.aiDisclosureMore"),
        aiDisclosureLess: t("blue.aiDisclosureLess"),
      },
      // Branchement HTTP réel (la clé LLM reste côté serveur).
      provider: "http",
      httpEndpoint: `${process.env.REACT_APP_BACKEND_URL}/api/blue/message`,
      httpTimeoutMs: 25000,
      defaultLocale: locale,
    }),
    [t, locale]
  );

  if (isExcludedRoute(location.pathname)) return null;

  return (
    <BlueAssistant
      key={locale}
      pageContext={pageContext}
      getPageContext={getPageContext}
      config={blueConfig}
    />
  );
}
